import React from 'react'
import { useTranslations } from 'next-intl'
import { useForm } from 'react-hook-form'

import { useCreateApplication } from '@/modules/settings/hooks/useApplications'
import type { PostApplicationFields } from '@/shared/models/applicationModel'
import { useDialogState } from '@/shared/hooks/useDialogState'
import { inputMessageContext, inputRequiredContext, inputStateContext } from '@/shared/utils/inputUtils'
import Button from '@/shared/v2/shared/components/Button'
import LoadingButton from '@/shared/v2/shared/components/LoadingButton'

import {
    DrawerBody,
    DrawerClose,
    DrawerContainer,
    DrawerContent,
    DrawerFooter,
    DrawerHeader,
    DrawerRoot,
    DrawerTrigger
} from '@/shared/v2/shared/components/Drawer/Drawer'
import Input from '@/shared/v2/shared/components/Input'
import { useSetToast } from '@/shared/hooks/useSetToast'
import PrivilegesButton from '@/shared/v2/shared/components/PrivilegesButton'

const CreateApplicationDrawer = () => {
    const authenticationTranslations = useTranslations('authentication')
    const toasterTranslations = useTranslations('toasters')
    const createApplication = useCreateApplication()
    const setToast = useSetToast()

    const formMethods = useForm<PostApplicationFields>()
    const {
        handleSubmit,
        register,
        watch,
        reset,
        formState: { errors }
    } = formMethods

    const getInputState = inputStateContext<PostApplicationFields>(watch, errors)
    const getInputMessage = inputMessageContext<PostApplicationFields>(errors)
    const getRequiredValue = inputRequiredContext()

    const { isOpen, onOpenChange } = useDialogState({
        onClose: reset,
        shouldCloseIf: createApplication.status === 'success'
    })

    const onCreateApplication = async (payload: PostApplicationFields) => {
        try {
            await createApplication.mutateAsync(payload)
            setToast({
                title: toasterTranslations('postApplication.positive.title'),
                description: toasterTranslations('postApplication.positive.description'),
                intent: 'success'
            })
        } catch (error) {
            setToast({
                title: toasterTranslations('postApplication.negative.title'),
                description: toasterTranslations('postApplication.negative.description'),
                intent: 'error'
            })
        }
    }

    return (
        <DrawerRoot open={isOpen} onOpenChange={onOpenChange}>
            <DrawerTrigger asChild>
                <PrivilegesButton
                    requiredPrivilege="manager"
                    data-testid="__create-application-trigger__"
                    className="whitespace-nowrap font-sans text-[14px]"
                >
                    {authenticationTranslations('applications.post.actions.open')}
                </PrivilegesButton>
            </DrawerTrigger>

            <DrawerContent>
                <DrawerContainer as="form" onSubmit={handleSubmit(onCreateApplication)}>
                    <DrawerHeader>{authenticationTranslations('applications.post.actions.open')}</DrawerHeader>

                    <DrawerBody>
                        <div className="grid grid-cols-1 gap-6">
                            <Input
                                data-testid="__create-application-name-input__"
                                label={authenticationTranslations('applications.post.inputs.name.label')}
                                placeholder={authenticationTranslations('applications.post.inputs.name.placeholder')}
                                state={getInputState('name')}
                                message={getInputMessage('name')}
                                {...register('name', {
                                    required: getRequiredValue()
                                })}
                            />

                            <Input
                                data-testid="__create-application-description-input__"
                                required={false}
                                placeholder={authenticationTranslations(
                                    'applications.post.inputs.description.placeholder'
                                )}
                                label={authenticationTranslations('applications.post.inputs.description.label')}
                                state={getInputState('description')}
                                {...register('description')}
                            />
                        </div>
                    </DrawerBody>

                    <DrawerFooter>
                        <div className="absolute left-0 bottom-0 w-full p-6 grid grid-cols-2 gap-x-3">
                            <DrawerClose asChild>
                                <Button intent={'secondary'}>
                                    {authenticationTranslations('applications.post.actions.cancel')}
                                </Button>
                            </DrawerClose>

                            <LoadingButton
                                data-testid="__create-application-submit__"
                                intent="primary"
                                type="submit"
                                isLoading={createApplication.status === 'pending'}
                            >
                                {authenticationTranslations('applications.post.actions.submit')}
                            </LoadingButton>
                        </div>
                    </DrawerFooter>
                </DrawerContainer>
            </DrawerContent>
        </DrawerRoot>
    )
}

export default CreateApplicationDrawer
